/**
 * `SYNC_DAILY_TO_MASTER` step. Single-call wrapper over
 * `POST /api/v2/route/{routeId}/master/stops/sync-daily`. For each target
 * date the BE rebuilds the daily sequence as `daily-not-on-master` at top
 * + mastered stops in master order. Explicit `serviceDates` override
 * `syncFuture` BE-side.
 */
import { err, ok } from 'types/entities/shared/result'

import { extractBackendError } from '../backendError'

import type { DayOfWeek, RouteId, ServiceDate } from 'types/entities/shared/ids'
import type { ApiDayOfWeek } from 'types/api/route/masterStopRequests'
import type { Deps, PipelineStep } from 'types/modules/routing'

import type { StepResult } from '../types'

export interface SyncDailyToMasterStepArgs {
  readonly routeId: RouteId
  readonly dayOfWeek: DayOfWeek
  readonly serviceDates?: ReadonlyArray<ServiceDate>
  readonly syncFuture?: boolean
}

export type SyncDailyToMasterStepValue = {
  readonly serviceDates: ReadonlyArray<string>
  readonly foundCount: number
  readonly notFoundCount: number
}

export type SyncDailyToMasterStepResult = StepResult<'SYNC_DAILY_TO_MASTER', SyncDailyToMasterStepValue>

export const makeSyncDailyToMasterStep = (
  args: SyncDailyToMasterStepArgs,
  deps: Deps,
): PipelineStep<'SYNC_DAILY_TO_MASTER', SyncDailyToMasterStepResult> => {
  const stepId = `sync-daily-to-master:${args.routeId}:${args.dayOfWeek}`
  const dayOfWeek = args.dayOfWeek as ApiDayOfWeek
  return {
    id: stepId,
    phase: 'SYNC_DAILY_TO_MASTER',
    label: 'Failed to sync daily routes to master sequence',
    applyOrder: 200,
    run: async ctx => {
      try {
        const response = await deps
          .syncDailyToMaster({
            routeId: args.routeId,
            data: {
              dayOfWeek,
              serviceDates: args.serviceDates ? [...args.serviceDates] : null,
              syncFuture: args.syncFuture ?? null,
              versionContext: ctx.versionContext,
            },
            skipInvalidate: true,
          })
          .unwrap()
        return ok({
          phase: 'SYNC_DAILY_TO_MASTER',
          stepId,
          value: {
            serviceDates: response?.serviceDates ?? [],
            foundCount: response?.foundCount ?? 0,
            notFoundCount: response?.notFoundCount ?? 0,
          },
        })
      } catch (cause: unknown) {
        const { status, message } = extractBackendError(cause)
        return err({
          kind: 'BE_REJECTED',
          phase: 'SYNC_DAILY_TO_MASTER',
          status,
          message,
        })
      }
    },
  }
}
